import { FormDrawer, FormDrawerContent, Id, LaravelResponse, isSuccess, useToasts } from "@dgs/core";
import { AxiosResponse } from "axios";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { AttachUserGroupsEventRequest, EventUserGroupFormValues } from "../../types/user";
import { EventUserGroupMultiSelect } from "./EventUserGroupMultiSelect";
import { UserResponsibilityByEventSelect } from "./UserResponsibilitySelect";

const initialValues: EventUserGroupFormValues = {
	userGroups: [],
	userResponsibilities: [],
};

interface Props {
	eventId: Id;
	reloadList: () => void;
	attachUserGroups: (values: AttachUserGroupsEventRequest) => Promise<AxiosResponse<LaravelResponse<void>>>;
	open: boolean;
	handleClose: () => void;
}

export const EventUserGroupAttachAction: FC<Props> = ({ eventId, reloadList, attachUserGroups, open, handleClose }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();

	const handleSubmit = useCallback(
		async (values: EventUserGroupFormValues) => {
			const response = await attachUserGroups({
				userGroupIds: values.userGroups.map((userGroup) => userGroup.id),
				userResponsibilityIds: values.userResponsibilities.map(({ id }) => id),
			});

			if (isSuccess(response)) {
				showToast({ type: "success", body: t("Users were successfully attached."), title: t("Attached users") });
				reloadList();
				return true;
			}

			return false;
		},
		[reloadList, showToast, t, attachUserGroups],
	);

	const validate = useCallback(
		(values: EventUserGroupFormValues) => {
			const errors: { [K in keyof EventUserGroupFormValues]?: any } = {};

			if (values.userGroups.length === 0) {
				errors.userGroups = t("Required field");
			}
			if (values.userResponsibilities.length === 0) {
				errors.userResponsibilities = t("Required field");
			}
			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			open={open}
			validate={validate}
			onSubmit={handleSubmit}
			initialValues={initialValues}
			onClose={handleClose}
			title={t("Attach user groups")}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<EventUserGroupMultiSelect eventId={eventId} name="userGroups" label={t("New user groups")} required={true} />
					<UserResponsibilityByEventSelect
						eventId={eventId}
						label={t("Responsibility")}
						name="userResponsibilities"
						required={true}
					/>
				</FormDrawerContent>
			)}
		/>
	);
};
